/**
 * Tiempo dedicado (paso 93): leer lo que escribe el técnico y enseñarlo.
 *
 * Se guardan los minutos EXACTOS. El redondeo a bloques (cuartos, medias
 * horas) es del contrato y del informe (paso 88): no se redondea aquí.
 */

/** El mismo techo que el backend (`MAX_WORKLOG_MINUTES`): más de un día por
 *  apunte es una errata. */
export const MAX_WORKLOG_MINUTES = 24 * 60;

/**
 * «25», «25 min», «1h», «1 h 30», «1h30m», «1:30», «1,5 h» → minutos.
 * `null` si no se entiende o se sale de 1…`max` (1440 por defecto: un apunte;
 * el plazo de un pack de SLA, paso 89, pasa su propio techo).
 *
 * Un número SIN unidad son minutos («25»), y con decimales sin unidad no se
 * acepta («1,5»): ¿hora y media o minuto y medio? Mejor preguntar que facturar
 * mal.
 */
export function parseDuration(raw: string, max: number = MAX_WORKLOG_MINUTES): number | null {
  const s = raw.trim().toLowerCase().replace(/\s+/g, ' ');
  if (!s) return null;
  let m: RegExpMatchArray | null;
  let minutes: number | null = null;
  if ((m = s.match(/^(\d+) ?(m|min|mins|minuto|minutos)?$/))) {
    minutes = Number(m[1]);
  } else if ((m = s.match(/^(\d+(?:[.,]\d+)?) ?(h|hora|horas)$/))) {
    minutes = Math.round(Number((m[1] ?? '').replace(',', '.')) * 60);
  } else if ((m = s.match(/^(\d+) ?(?:h|hora|horas) ?(\d+) ?(m|min|mins|minuto|minutos)?$/))) {
    if (Number(m[2]) >= 60) return null;
    minutes = Number(m[1]) * 60 + Number(m[2]);
  } else if ((m = s.match(/^(\d+):(\d{2})$/))) {
    if (Number(m[2]) >= 60) return null;
    minutes = Number(m[1]) * 60 + Number(m[2]);
  }
  if (minutes === null || minutes < 1 || minutes > max) return null;
  return minutes;
}

/** 25 → «25 min» · 60 → «1 h» · 85 → «1 h 25 min». */
export function formatMinutes(total: number): string {
  const h = Math.floor(total / 60);
  const min = total % 60;
  if (h === 0) return `${min} min`;
  if (min === 0) return `${h} h`;
  return `${h} h ${min} min`;
}

/** Para volver a editar un apunte: el formato que `parseDuration` lee. */
export function minutesToInput(total: number): string {
  const h = Math.floor(total / 60);
  const min = total % 60;
  if (h === 0) return String(min);
  return min === 0 ? `${h}h` : `${h}h${String(min).padStart(2, '0')}`;
}

/** Hoy en Madrid (YYYY-MM-DD), el mismo día que pone el servidor por defecto.
 *  `en-CA` es el locale que formatea así; no es el idioma de la pantalla. */
export function todayMadrid(): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Madrid' }).format(new Date());
}

/** «2026-10-04» → «4 oct» (y el año solo si no es el de hoy). Se lee como
 *  fecha de calendario, sin zona: es el DÍA del trabajo, no un instante. */
export function formatWorkedOn(iso: string): string {
  const [y = 0, mo = 1, d = 1] = iso.split('-').map(Number);
  const date = new Date(Date.UTC(y, mo - 1, d));
  const sameYear = y === Number(todayMadrid().slice(0, 4));
  return date.toLocaleDateString('es-ES', {
    day: 'numeric',
    month: 'short',
    ...(sameYear ? {} : { year: 'numeric' }),
    timeZone: 'UTC',
  }).replace('.', '');
}
